import { Button, Typography } from '@breetometer/components';
import { useModalApi } from 'BreetHooks';
import { selectTourStep, setTourStep, useAppDispatch, useAppSelector } from 'BreetRedux';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';

import { COACHMARK_GAP, COACHMARK_WIDTH, SPOTLIGHT_PADDING, tourSteps, VIEWPORT_MARGIN } from './SpotlightTour.helpers';

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const SpotlightTour = () => {
	const step = useAppSelector(selectTourStep);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { closeModalDirect } = useModalApi();

	const [rect, setRect] = useState<DOMRect | null>(null);
	const [coachHeight, setCoachHeight] = useState(0);

	const coachRef = useRef<HTMLDivElement>(null);

	const config = step ? tourSteps[step] : null;

	useLayoutEffect(() => {
		if (!config) {
			setRect(null);
			return;
		}

		const measure = () => {
			const el = document.querySelector(config.selector);
			setRect(el ? el.getBoundingClientRect() : null);
			return !!el;
		};

		let pollId: ReturnType<typeof setInterval> | undefined;
		if (!measure()) {
			let attempts = 0;
			pollId = setInterval(() => {
				attempts += 1;
				if (measure() || attempts > 50) clearInterval(pollId);
			}, 100);
		}

		window.addEventListener('resize', measure);
		window.addEventListener('scroll', measure, true);

		return () => {
			clearInterval(pollId);
			window.removeEventListener('resize', measure);
			window.removeEventListener('scroll', measure, true);
		};
	}, [config]);

	// Measure the coachmark so we can keep its bottom edge inside the viewport
	// when the anchored target sits low on screen (e.g. footer menu items).
	useLayoutEffect(() => {
		setCoachHeight(coachRef.current?.offsetHeight ?? 0);
	}, [config, rect]);

	const close = useCallback(() => dispatch(setTourStep(null)), [dispatch]);

	const handleCta = useCallback(() => {
		if (config?.ctaPath) {
			navigate(config.ctaPath)?.catch(console.error);
			closeModalDirect('onBoardingTodoList');
		}
		if (config?.nextStep) {
			dispatch(setTourStep(config.nextStep));
		} else {
			close();
		}
	}, [config, navigate, closeModalDirect, dispatch, close]);

	if (!config || !rect) return null;

	const isBottom = config.placement === 'bottom';

	const cutoutStyle = {
		top: rect.top - SPOTLIGHT_PADDING,
		left: rect.left - SPOTLIGHT_PADDING,
		width: rect.width + SPOTLIGHT_PADDING * 2,
		height: rect.height + SPOTLIGHT_PADDING * 2,
	};

	const rawLeft = isBottom ? rect.left : rect.right + COACHMARK_GAP;
	const rawTop = isBottom ? rect.bottom + COACHMARK_GAP : rect.top;
	const left = clamp(rawLeft, VIEWPORT_MARGIN, window.innerWidth - COACHMARK_WIDTH - VIEWPORT_MARGIN);
	const maxTop = Math.max(VIEWPORT_MARGIN, window.innerHeight - coachHeight - VIEWPORT_MARGIN);
	const top = clamp(rawTop, VIEWPORT_MARGIN, maxTop);

	const arrowStyle = isBottom
		? { left: clamp(rect.left + rect.width / 2 - left, 20, COACHMARK_WIDTH - 20) }
		: { top: clamp(rect.top + rect.height / 2 - top, 20, 200) };

	const showSteps = !!config.step && !!config.steps;

	return createPortal(
		<div className='spotlight-tour'>
			<div
				className='spotlight-cutout'
				style={cutoutStyle}
			/>

			<div
				ref={coachRef}
				className={`spotlight-coachmark spotlight-coachmark--${config.placement}`}
				style={{ top, left, width: COACHMARK_WIDTH }}
			>
				<span
					className='spotlight-arrow'
					style={arrowStyle}
				/>

				<Typography
					as='h6'
					className='spotlight-title color-tertiary-400'
				>
					{config.title}
				</Typography>
				<Typography className='spotlight-desc color-primary-50'>{config.description}</Typography>

				<div className='spotlight-actions'>
					{showSteps && (
						<span className='spotlight-step color-primary-200'>
							Step {config.step} of {config.steps}
						</span>
					)}

					<div className='spotlight-buttons'>
						<Button
							variant='native'
							className='spotlight-skip'
							onClick={close}
						>
							Skip
						</Button>
						<Button onClick={handleCta}>{config.ctaLabel}</Button>
					</div>
				</div>
			</div>
		</div>,
		document.body
	);
};

export default SpotlightTour;
